import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import StoryCard from '../components/StoryCard'
import { useBlocks } from '../hooks/useBlocks'
import { useAuth } from '../context/AuthContext'
import SEO from '../components/SEO'
import SkeletonLoader from '../components/SkeletonLoader'
import PullToRefresh from '../components/PullToRefresh'
import BackToTop from '../components/BackToTop'
import { getFandomMeta } from './FandomsPage'
import { getFandomIcon } from '../lib/fandomIcons'

// ─── Constants ─────────────────────────────────────────────────────────────────

const ALL_GENRES  = ['Fantasy', 'Romance', 'Sci-Fi', 'Anime', 'Mystery', 'Horror', 'Adventure', 'Drama', 'Comedy', 'Historical', 'Slice of Life', 'Other']
const ALL_RATINGS = ['Teen', 'Mature']
const ALL_STATUSES = ['Ongoing', 'Complete', 'Hiatus']
const SORT_OPTIONS = [
  { value: 'newest',    label: 'Recently updated' },
  { value: 'trending',  label: 'Trending' },
  { value: 'liked',     label: 'Most liked' },
  { value: 'views',     label: 'Most read' },
  { value: 'az',        label: 'A – Z' },
]

const PAGE_SIZE = 30

const FANDOM_ACCENTS = [
  'var(--tag-fandom-text)',
  'var(--tag-genre-text)',
  'var(--tag-trope-text)',
  'var(--tag-rating-text)',
]

// ─── Main component ─────────────────────────────────────────────────────────────

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [totalStories, setTotalStories] = useState(0)
  const [fandoms, setFandoms] = useState([])
  const [allTags, setAllTags] = useState([])
  const blockedIds = useBlocks()
  const { getToken } = useAuth()

  // Controlled search input (debounced into URL)
  const [inputVal, setInputVal] = useState(searchParams.get('q') ?? '')
  const debounceRef = useRef(null)

  // Autocomplete
  const [suggOpen,     setSuggOpen]     = useState(false)
  const [activeSuggIdx, setActiveSuggIdx] = useState(-1)
  const searchWrapRef = useRef(null)

  const q      = searchParams.get('q')      ?? ''
  const genre  = searchParams.get('genre')  ?? ''
  const fandom = searchParams.get('fandom') ?? ''
  const tag    = searchParams.get('tag')    ?? ''
  const rating = searchParams.get('rating') ?? ''
  const status = searchParams.get('status') ?? ''
  const sort   = searchParams.get('sort')   ?? 'newest'

  // Load browse metadata (fandoms + tags) once
  useEffect(() => {
    axios.get('/api/stories/browse-meta')
      .then(r => {
        setFandoms(r.data.fandoms ?? [])
        setAllTags(r.data.tags ?? [])
        setTotalStories(r.data.totalStories ?? 0)
      })
      .catch(() => {})
  }, [])

  const buildParams = useCallback((offset = 0) => {
    const params = { limit: PAGE_SIZE, offset }
    if (q)      params.search = q
    if (genre)  params.genre = genre
    if (fandom) params.fandom = fandom
    if (tag)    params.tag = tag
    if (rating) params.rating = rating
    if (status) params.status = status
    if (sort)   params.sort = sort
    return params
  }, [q, genre, fandom, tag, rating, status, sort])

  const fetchStories = useCallback(async (offset = 0) => {
    const token = await getToken().catch(() => null)
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const { data } = await axios.get('/api/stories', { params: buildParams(offset), headers })
    return data ?? []
  }, [getToken, buildParams])

  // Reload when filters/sort change
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setHasMore(true)
    fetchStories(0)
      .then(data => {
        if (cancelled) return
        setStories(data)
        setHasMore(data.length === PAGE_SIZE)
      })
      .catch(() => { if (!cancelled) setStories([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [fetchStories])

  async function loadMore() {
    setLoadingMore(true)
    try {
      const data = await fetchStories(stories.length)
      setStories(prev => [...prev, ...data])
      setHasMore(data.length === PAGE_SIZE)
    } catch {}
    setLoadingMore(false)
  }

  async function handleRefresh() {
    const [storyData, metaData] = await Promise.all([
      fetchStories(0),
      axios.get('/api/stories/browse-meta').then(r => r.data).catch(() => null),
    ])
    setStories(storyData)
    setHasMore(storyData.length === PAGE_SIZE)
    if (metaData) {
      setFandoms(metaData.fandoms ?? [])
      setAllTags(metaData.tags ?? [])
      setTotalStories(metaData.totalStories ?? 0)
    }
  }

  // Close autocomplete on outside click
  useEffect(() => {
    function handler(e) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSuggOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  function clearAll() {
    setInputVal('')
    setSearchParams({}, { replace: true })
  }

  function handleSearchChange(val) {
    setInputVal(val)
    setActiveSuggIdx(-1)
    setSuggOpen(val.trim().length > 0)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setParam('q', val), 280)
  }

  // Compute autocomplete suggestions from current input
  const flatSuggs = useMemo(() => {
    const needle = inputVal.toLowerCase().trim()
    if (!needle) return []

    const fandomMatches = fandoms
      .filter((f) => f.name.toLowerCase().includes(needle))
      .slice(0, 5)
      .map((f) => ({ type: 'fandom', label: f.name, count: f.count }))

    const tagMatches = allTags
      .filter((t) => t.toLowerCase().includes(needle))
      .slice(0, 5)
      .map((t) => ({ type: 'tag', label: t }))

    return [...fandomMatches, ...tagMatches]
  }, [inputVal, fandoms, allTags])

  function selectSugg(sugg) {
    setSuggOpen(false)
    setActiveSuggIdx(-1)
    clearTimeout(debounceRef.current)
    if (sugg.type === 'fandom') {
      setInputVal('')
      const next = new URLSearchParams(searchParams)
      next.set('fandom', sugg.label)
      next.delete('q')
      setSearchParams(next, { replace: true })
    } else {
      setInputVal('')
      const next = new URLSearchParams(searchParams)
      next.set('tag', sugg.label)
      next.delete('q')
      setSearchParams(next, { replace: true })
    }
  }

  function handleKeyDown(e) {
    if (!suggOpen || flatSuggs.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveSuggIdx((i) => Math.min(i + 1, flatSuggs.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveSuggIdx((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && activeSuggIdx >= 0) {
      e.preventDefault()
      selectSugg(flatSuggs[activeSuggIdx])
    } else if (e.key === 'Escape') {
      setSuggOpen(false)
      setActiveSuggIdx(-1)
    }
  }

  const results = stories.filter(s => !blockedIds.has(s.userId))

  const activeFilterCount = [q, genre, fandom, tag, rating, status].filter(Boolean).length
  const noFilters = activeFilterCount === 0

  return (
    <div className="browse-page">
      <SEO
        title="Browse Stories"
        description="Discover thousands of stories, fanfiction, and original fiction across every genre on Verse. Filter by genre, fandom, rating, and more."
      />
      <Navbar />

      <PullToRefresh onRefresh={handleRefresh}>
      {/* ── Hero ── */}
      <div className="browse-hero">
        <div className="browse-hero__bg">
          <div className="browse-hero__blob browse-hero__blob--1" />
          <div className="browse-hero__blob browse-hero__blob--2" />
          <div className="browse-hero__blob browse-hero__blob--3" />
        </div>

        <div className="browse-hero__inner">
          <p className="browse-hero__eyebrow">✦ {totalStories} stories and counting</p>
          <h1 className="browse-hero__title">Discover your next obsession</h1>

          {/* Search bar + autocomplete */}
          <div className="browse-search-wrap" ref={searchWrapRef}>
            <div className={`browse-search${suggOpen && flatSuggs.length > 0 ? ' browse-search--open' : ''}`}>
              <svg className="browse-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                className="browse-search__input"
                aria-label="Search stories"
                placeholder="Search by title, author, fandom, or tag…"
                value={inputVal}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => { if (inputVal.trim()) setSuggOpen(true) }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                autoFocus
                aria-autocomplete="list"
                aria-expanded={suggOpen && flatSuggs.length > 0}
              />
              {inputVal && (
                <button className="browse-search__clear" aria-label="Clear search" onClick={() => { handleSearchChange(''); setSuggOpen(false) }}>×</button>
              )}
            </div>

            {/* Autocomplete dropdown */}
            {suggOpen && flatSuggs.length > 0 && (
              <div className="browse-ac" role="listbox">
                {/* Section: Fandoms */}
                {flatSuggs.some((s) => s.type === 'fandom') && (
                  <>
                    <div className="browse-ac__section">Fandoms</div>
                    {flatSuggs.filter((s) => s.type === 'fandom').map((s, i) => {
                      const idx = flatSuggs.indexOf(s)
                      const emoji = getFandomIcon(s.label) ?? getFandomMeta(s.label).emoji
                      return (
                        <button
                          key={s.label}
                          className={`browse-ac__item${activeSuggIdx === idx ? ' browse-ac__item--active' : ''}`}
                          role="option"
                          aria-selected={activeSuggIdx === idx}
                          onMouseEnter={() => setActiveSuggIdx(idx)}
                          onMouseDown={(e) => { e.preventDefault(); selectSugg(s) }}
                        >
                          <span className="browse-ac__emoji">{emoji}</span>
                          <span className="browse-ac__label">{s.label}</span>
                          <span className="browse-ac__meta">{s.count} {s.count === 1 ? 'story' : 'stories'}</span>
                        </button>
                      )
                    })}
                  </>
                )}

                {/* Section: Tags */}
                {flatSuggs.some((s) => s.type === 'tag') && (
                  <>
                    <div className="browse-ac__section">Tags</div>
                    {flatSuggs.filter((s) => s.type === 'tag').map((s) => {
                      const idx = flatSuggs.indexOf(s)
                      return (
                        <button
                          key={s.label}
                          className={`browse-ac__item${activeSuggIdx === idx ? ' browse-ac__item--active' : ''}`}
                          role="option"
                          aria-selected={activeSuggIdx === idx}
                          onMouseEnter={() => setActiveSuggIdx(idx)}
                          onMouseDown={(e) => { e.preventDefault(); selectSugg(s) }}
                        >
                          <span className="browse-ac__tag-hash">#</span>
                          <span className="browse-ac__label">{s.label}</span>
                        </button>
                      )
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Quick genre links */}
          <div className="browse-quick-genres">
            {['Fantasy', 'Romance', 'Anime', 'Sci-Fi', 'Mystery', 'Horror', 'Historical', 'Comedy'].map((g) => (
              <button
                key={g}
                className={`browse-quick-genre${genre === g ? ' browse-quick-genre--active' : ''}`}
                onClick={() => setParam('genre', genre === g ? '' : g)}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sticky filter bar ── */}
      <div className="browse-filter-bar">
        <div className="browse-filter-bar__inner">
          {/* Dropdowns */}
          <FilterSelect
            label="Genre"
            value={genre}
            options={ALL_GENRES}
            onChange={(v) => setParam('genre', v)}
          />
          <FilterSelect
            label="Fandom"
            value={fandom}
            options={fandoms.map((f) => f.name)}
            onChange={(v) => setParam('fandom', v)}
          />
          <FilterSelect
            label="Rating"
            value={rating}
            options={ALL_RATINGS}
            onChange={(v) => setParam('rating', v)}
          />
          <FilterSelect
            label="Status"
            value={status}
            options={ALL_STATUSES}
            onChange={(v) => setParam('status', v)}
          />

          <div className="browse-filter-bar__spacer" />

          {/* Sort */}
          <div className="browse-sort">
            <label className="browse-sort__label" htmlFor="browse-sort">Sort</label>
            <select
              id="browse-sort"
              className="browse-sort__select"
              value={sort}
              onChange={(e) => setParam('sort', e.target.value)}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Clear */}
          {activeFilterCount > 0 && (
            <button className="browse-clear-btn" onClick={clearAll}>
              Clear {activeFilterCount > 1 ? `(${activeFilterCount})` : ''}
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="browse-active-filters">
            {q      && <ActiveChip label={`"${q}"`}  onRemove={() => { setInputVal(''); setParam('q', '')      }} />}
            {genre  && <ActiveChip label={genre}       onRemove={() => setParam('genre',  '')} />}
            {fandom && <ActiveChip label={fandom}      onRemove={() => setParam('fandom', '')} />}
            {tag    && <ActiveChip label={`#${tag}`}    onRemove={() => setParam('tag',    '')} />}
            {rating && <ActiveChip label={rating}      onRemove={() => setParam('rating', '')} />}
            {status && <ActiveChip label={status}      onRemove={() => setParam('status', '')} />}
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div className="browse-main">

        {/* Fandom spotlight — only when no filters active */}
        {noFilters && fandoms.length > 0 && (
          <div className="fandom-spotlight">
            <div className="fandom-spotlight__header">
              <h2 className="fandom-spotlight__title">Browse by fandom</h2>
              <span className="fandom-spotlight__count">{fandoms.length} fandoms</span>
            </div>
            <div className="fandom-spotlight__scroll">
              {fandoms.map(({ name, count }, i) => (
                <button
                  key={name}
                  className="fandom-card"
                  onClick={() => setParam('fandom', name)}
                >
                  {getFandomIcon(name)
                    ? <span style={{ fontSize: 16, lineHeight: 1 }}>{getFandomIcon(name)}</span>
                    : <div className="fandom-card__dot" style={{ background: FANDOM_ACCENTS[i % FANDOM_ACCENTS.length] }} />
                  }
                  <span className="fandom-card__name">{name}</span>
                  <span className="fandom-card__count">{count} {count === 1 ? 'story' : 'stories'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results header */}
        <div className="browse-results-header">
          <span className="browse-results-count">
            {results.length === 0 && !loading
              ? 'No stories found'
              : `${results.length}${hasMore ? '+' : ''} ${results.length === 1 ? 'story' : 'stories'}`
            }
            {!noFilters && ' matching your filters'}
          </span>
        </div>

        {/* Grid */}
        {loading ? (
          <SkeletonLoader type="story-grid" />
        ) : results.length > 0 ? (
          <>
            <div className="story-grid story-grid--dense">
              {results.map((story, i) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  index={i}
                  onGenreClick={(g) => setParam('genre', g)}
                  onFandomClick={(f) => setParam('fandom', f)}
                />
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                <button
                  className="btn btn-outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{ fontSize: '0.9rem', padding: '10px 32px' }}
                >
                  {loadingMore ? 'Loading…' : 'Load more stories'}
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState onClear={clearAll} hasFilters={activeFilterCount > 0} fandomFilter={fandom} />
        )}
      </div>
      </PullToRefresh>
      <BackToTop />
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div className="filter-select-wrap">
      <select
        className={`filter-select${value ? ' filter-select--active' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{label}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {value && <span className="filter-select__dot" />}
    </div>
  )
}

function ActiveChip({ label, onRemove }) {
  return (
    <span className="browse-active-chip">
      {label}
      <button className="browse-active-chip__remove" aria-label={`Remove filter: ${label}`} onClick={onRemove}>×</button>
    </span>
  )
}

const STARTER_STEPS = [
  { icon: '✍️', title: 'Write your first story', desc: 'Hit "Write" in the nav bar, fill in your title, fandom, and rating, then publish.', action: { label: 'Start writing', to: '/write' } },
  { icon: '🏷️', title: 'Tag it properly',        desc: 'Add a fandom, content rating (Teen / Mature), and status so readers can find it.' },
  { icon: '👥', title: 'Join a community group', desc: 'Find your fandom\'s group in Community and share your story there for your first readers.', action: { label: 'Browse groups', to: '/community' } },
  { icon: '📣', title: 'Follow other writers',   desc: 'Follow people writing in your fandom — they\'ll often follow back and read your work.' },
]

const REQUESTED_FANDOMS = [
  { name: 'Solo Leveling',       emoji: '⚔️',  genre: 'Anime / Manhwa' },
  { name: 'Hazbin Hotel',        emoji: '😈',  genre: 'Animation' },
  { name: 'Arcane',              emoji: '⚙️',  genre: 'TV / Animation' },
  { name: 'Chainsaw Man',        emoji: '🪚',  genre: 'Anime' },
  { name: 'The Owl House',       emoji: '🦉',  genre: 'Animation' },
  { name: 'Wednesday',           emoji: '🖤',  genre: 'TV' },
  { name: 'Heartstopper',        emoji: '🍂',  genre: 'TV / Books' },
  { name: 'Spy x Family',        emoji: '🌸',  genre: 'Anime' },
  { name: 'Good Omens',          emoji: '😇',  genre: 'TV / Books' },
  { name: 'Our Flag Means Death', emoji: '🏴‍☠️', genre: 'TV' },
  { name: 'Loki',                emoji: '🐍',  genre: 'Marvel' },
  { name: 'Scream (2022)',        emoji: '🔪',  genre: 'Movies' },
]

function EmptyState({ onClear, hasFilters, fandomFilter }) {
  if (!hasFilters) {
    // Site is completely empty — show the starter guide
    return (
      <div className="browse-starter">
        <div className="browse-starter__header">
          <div className="browse-starter__badge">New here?</div>
          <h2 className="browse-starter__title">Be the first to write a story</h2>
          <p className="browse-starter__sub">Verse is just getting started. Here's how to become one of its founding writers.</p>
        </div>

        <div className="browse-starter__steps">
          {STARTER_STEPS.map((step, i) => (
            <div key={i} className="browse-starter__step">
              <div className="browse-starter__step-icon">{step.icon}</div>
              <div className="browse-starter__step-body">
                <strong className="browse-starter__step-title">{step.title}</strong>
                <p className="browse-starter__step-desc">{step.desc}</p>
                {step.action && (
                  <Link to={step.action.to} className="browse-starter__step-link">
                    {step.action.label} →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="browse-starter__divider">
          <span>Fandoms waiting for their first story</span>
        </div>

        <div className="browse-requested">
          {REQUESTED_FANDOMS.map((f) => (
            <div key={f.name} className="browse-requested-fandom">
              <span className="browse-requested-fandom__emoji">{f.emoji}</span>
              <div className="browse-requested-fandom__info">
                <span className="browse-requested-fandom__name">{f.name}</span>
                <span className="browse-requested-fandom__genre">{f.genre}</span>
              </div>
              <Link
                to={`/write`}
                className="browse-requested-fandom__btn"
                title={`Write a ${f.name} story`}
              >
                Write first
              </Link>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Filters produced no results — show requested fandoms as inspiration
  return (
    <div className="browse-empty">
      <div className="browse-empty__icon">🔍</div>
      <h3 className="browse-empty__title">
        {fandomFilter ? `No "${fandomFilter}" stories yet` : 'No stories match your filters'}
      </h3>
      <p className="browse-empty__desc">
        {fandomFilter
          ? `This fandom has no stories yet — you could be the first one to write here!`
          : 'Try broadening your search, or be the first to write in this space.'}
      </p>
      <div className="browse-empty__actions">
        <button className="btn btn-outline" onClick={onClear}>Clear filters</button>
        <Link to="/write" className="btn btn-primary">Write a story</Link>
      </div>

      {fandomFilter && (
        <div className="browse-empty__inspire">
          <p className="browse-empty__inspire-label">Other fandoms waiting for writers</p>
          <div className="browse-requested browse-requested--compact">
            {REQUESTED_FANDOMS.filter(f => f.name !== fandomFilter).slice(0, 6).map((f) => (
              <div key={f.name} className="browse-requested-fandom">
                <span className="browse-requested-fandom__emoji">{f.emoji}</span>
                <div className="browse-requested-fandom__info">
                  <span className="browse-requested-fandom__name">{f.name}</span>
                  <span className="browse-requested-fandom__genre">{f.genre}</span>
                </div>
                <Link to="/write" className="browse-requested-fandom__btn">Write first</Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
